// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { updateLineInfo as updateLineInfoApi, UpdateLineInfo } from "@bg/App/Services/WishListService";
import {
    createHandlerChainRunner,
    Handler,
    markSkipOnCompleteIfOnErrorIsSet,
} from "@insite/client-framework/HandlerCreator";
import loadWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishList";
import loadWishListLines from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListLines";

interface Props {
    apiParameter: UpdateLineInfo;
    apiResult: { successful: boolean; error?: any };
}

type Parameter = UpdateLineInfo & {
    reloadWishList?: boolean;
    onComplete?: (result: any) => void;
    onError?: (e: any) => void;
};

type HandlerType = Handler<Parameter, Props>;

export const DispatchBeginAction: HandlerType = props => {
    const dataViewParameter = props.getState().pages.myListDetails.loadWishListLinesParameter;
    props.dispatch({
        type: "Data/WishListLines/BeginLoadWishListLines",
        parameter: dataViewParameter,
    });
    props.apiParameter = {
        wishListId: props.parameter.wishListId,
        linesModel: [...props.parameter.linesModel],
        ...(props.parameter.updateSortOrder && { updateSortOrder: true }),
        ...(props.parameter.updateQty && { updateQty: true }),
        ...(props.parameter.updateNote && { updateNote: true }),
    };
};

let counter = 0;
export const CallApiWithParameters: HandlerType = async props => {
    counter++;
    props.apiResult = await updateLineInfoApi(props.apiParameter);
    counter--;
};

export const ReloadWishListOnSuccess: HandlerType = props => {
    if (counter === 0 && props.apiResult.successful && props.parameter.reloadWishList) {
        props.dispatch(
            loadWishListLines({
                onSuccess: () => {
                    if (props.apiResult.successful && props.parameter.onComplete) {
                        props.parameter.onComplete(props.apiResult);
                    }
                },
            }),
        );
    }
};

export const CallOnComplete: HandlerType = props => {};

export const OnError: HandlerType = props => {
    if (props.apiResult && !props.apiResult.successful) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.dispatch({
            type: "Data/WishListLines/Reset",
        });
        props.dispatch({
            type: "Data/WishLists/Reset",
        });
        props.parameter.onError?.(props.apiResult?.error);
    }
};

export const chain = [DispatchBeginAction, CallApiWithParameters, ReloadWishListOnSuccess, CallOnComplete, OnError];

const updateLineInfo = createHandlerChainRunner(chain, "UpdateLineInfo");
export default updateLineInfo;
